import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCheckbox,
  IonCol,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonChip,
} from '@ionic/react';
import { cardOutline, heartOutline, logoApple, logoGoogle, closeCircle, checkmarkCircle } from 'ionicons/icons';
import React, { useState, useEffect } from 'react';
import Content from '../components/Content';
import { usePageTitle } from '../util/sugar';
import { redirectToCheckout } from '../services/StripeService';
import { useLocation } from 'react-router-dom';
import { APP_TITLE } from '../config/env';
import './DonatePage.scss';

type DonationType = 'monthly' | 'one-time';
type PaymentMethod = 'card' | 'apple-pay' | 'google-pay';

const DonatePage: React.FC = () => {
  usePageTitle(`Support ${APP_TITLE}`);
  const location = useLocation();

  // State
  const [donationType, setDonationType] = useState<DonationType>('monthly');
  const [selectedAmount, setSelectedAmount] = useState<number>(10);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [newsletter, setNewsletter] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Check for success/cancel query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      setSuccessMessage('Thank you for your donation! Your support means the world to us.');
    } else if (params.get('canceled') === 'true') {
      setErrorMessage('Donation canceled. Feel free to try again when you\'re ready.');
    }
  }, [location.search]);

  // Donation tiers
  const monthlyAmounts = [5, 10, 25, 50];
  const oneTimeAmounts = [10, 25, 50, 100];

  const amounts = donationType === 'monthly' ? monthlyAmounts : oneTimeAmounts;

  const handleAmountSelection = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleDonate = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    setSuccessMessage('');

    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;

    if (amount < 5) {
      setErrorMessage('Minimum donation is $5');
      setIsProcessing(false);
      return;
    }

    try {
      // Redirect to Stripe Checkout
      await redirectToCheckout({
        type: donationType,
        amount,
        newsletter,
      });
    } catch (error: any) {
      console.error('Donation error:', error);
      setErrorMessage(error.message || 'Failed to process donation. Please try again.');
      setIsProcessing(false);
    }
  };

  const getAmountDisplay = () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    return isNaN(amount) ? 0 : amount;
  };

  return (
    <Content>
      <IonRow>
        <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
          <IonCard className="donate-card">
            <IonCardHeader>
              <h1 className="donate-title">Support {APP_TITLE}</h1>
              <p className="donate-subtitle">
                Support the {APP_TITLE} platform and community. Donations go to covering hosting and development costs.
                Thanks for your contribution!
              </p>
            </IonCardHeader>

            <IonCardContent>
              {/* Success Message */}
              {successMessage && (
                <div className="message-section" role="status" aria-live="polite">
                  <IonChip color="success" className="message-chip">
                    <IonIcon icon={checkmarkCircle} aria-hidden="true" />
                    <IonLabel>{successMessage}</IonLabel>
                  </IonChip>
                </div>
              )}

              {/* Error Message */}
              {errorMessage && (
                <div className="message-section" role="alert" aria-live="assertive">
                  <IonChip color="danger" className="message-chip">
                    <IonIcon icon={closeCircle} aria-hidden="true" />
                    <IonLabel>{errorMessage}</IonLabel>
                  </IonChip>
                </div>
              )}

              {/* Donation Type Selection */}
              <div className="section">
                <h3 className="section-title">Select a donation type</h3>
                <div className="button-group">
                  <IonButton
                    expand="block"
                    fill={donationType === 'monthly' ? 'solid' : 'outline'}
                    onClick={() => setDonationType('monthly')}
                    className="type-button"
                  >
                    Monthly
                  </IonButton>
                  <IonButton
                    expand="block"
                    fill={donationType === 'one-time' ? 'solid' : 'outline'}
                    onClick={() => setDonationType('one-time')}
                    className="type-button"
                  >
                    One-Time
                  </IonButton>
                </div>
              </div>

              {/* Amount Selection */}
              <div className="section">
                <h3 className="section-title">Select or enter an amount</h3>
                <div className="amount-grid">
                  {amounts.map((amount) => (
                    <IonButton
                      key={amount}
                      expand="block"
                      fill={selectedAmount === amount ? 'solid' : 'outline'}
                      onClick={() => handleAmountSelection(amount)}
                      className="amount-button"
                    >
                      ${amount}
                    </IonButton>
                  ))}
                  <IonButton
                    expand="block"
                    fill={customAmount ? 'solid' : 'outline'}
                    className="amount-button custom-amount-button"
                    aria-label="Enter custom donation amount"
                  >
                    <IonInput
                      type="number"
                      placeholder="$ Other"
                      value={customAmount}
                      onIonInput={(e: any) => handleCustomAmount(e.detail.value || '')}
                      min="5"
                      step="1"
                      className="custom-amount-input"
                      aria-label="Custom donation amount in dollars"
                    />
                  </IonButton>
                </div>
              </div>

              {/* Newsletter Opt-in */}
              <div className="section">
                <IonItem lines="none" className="newsletter-item">
                  <IonCheckbox
                    aria-label="Newsletter opt-in"
                    slot="start"
                    checked={newsletter}
                    onIonChange={(e) => setNewsletter(e.detail.checked)}
                  />
                  <IonLabel className="ion-text-wrap">
                    Keep up to date with the latest news from {APP_TITLE}
                  </IonLabel>
                </IonItem>
              </div>

              {/* Payment Method Selection */}
              <div className="section">
                <h3 className="section-title" id="payment-method-label">Select your payment method</h3>
                <IonRadioGroup
                  value={paymentMethod}
                  onIonChange={(e) => setPaymentMethod(e.detail.value)}
                  aria-labelledby="payment-method-label"
                >
                  <IonItem lines="none">
                    <IonIcon icon={cardOutline} slot="start" aria-hidden="true" />
                    <IonLabel>Credit Card</IonLabel>
                    <IonRadio slot="end" value="card" aria-label="Credit Card" />
                  </IonItem>
                  <IonItem lines="none">
                    <IonIcon icon={logoApple} slot="start" aria-hidden="true" />
                    <IonLabel>Apple Pay</IonLabel>
                    <IonRadio slot="end" value="apple-pay" aria-label="Apple Pay" />
                  </IonItem>
                  <IonItem lines="none">
                    <IonIcon icon={logoGoogle} slot="start" aria-hidden="true" />
                    <IonLabel>Google Pay</IonLabel>
                    <IonRadio slot="end" value="google-pay" aria-label="Google Pay" />
                  </IonItem>
                </IonRadioGroup>
              </div>

              {/* Donate Button */}
              <div className="section">
                <IonButton
                  expand="block"
                  size="large"
                  onClick={handleDonate}
                  disabled={isProcessing || getAmountDisplay() < 5}
                  className="donate-button"
                >
                  <IonIcon icon={heartOutline} slot="start" aria-hidden="true" />
                  {isProcessing
                    ? 'Processing...'
                    : `Donate ${donationType === 'monthly' ? 'Monthly' : 'Now'} - $${getAmountDisplay()}`}
                </IonButton>
              </div>

              {/* Terms and Privacy */}
              <div className="terms-section">
                <small>
                  By donating, you agree to our terms of service and privacy policy.
                  Your payment information is securely processed through Stripe.
                </small>
              </div>
            </IonCardContent>
          </IonCard>
        </IonCol>
      </IonRow>
    </Content>
  );
};

export default DonatePage;
